import React from 'react';
import { graphql } from 'gatsby';
import { HelmetDatoCms } from 'gatsby-source-datocms';
import Layout from '../components/Layout';
import BlogDetailBanner from '../components/BlogDetailBanner';
import BlogDetail from '../components/BlogDetail';
import ModularBlocks from '../components/ModularBlocks';
import FeaturedBlogPosts from '../components/FeaturedBlogPosts';
import CallToAction from '../components/CallToAction';
import FeaturedResourcePopUp from '../components/FeaturedResourcePopUp';
import { buildUrl, slugify } from '../utils';

const BlogDetailPageTemplate = ({
  data: {
    datoCmsBlog,
    datoCmsBlogSitewide: { ctaImage, ctaHeading, ctaText, ctaLink },
    allDatoCmsBlog: { otherBlogPosts },
    allDatoCmsBlogCategory: { blogCategories },
  },
}) => {
  const {
    seoMetaTags,
    title,
    categories,
    featuredImage,
    author,
    introduction,
    modularBlocks,
    summary,
    summaryNew,
    featuredResource,
    meta,
  } = datoCmsBlog;

  const anchorLinks = modularBlocks
    .map(
      ({ heading }) =>
        heading && {
          text: heading,
          url: slugify(heading),
        },
    )
    .filter(modularBlock => modularBlock && modularBlock.text !== undefined);

  const orderBlogCategories = (array, order) =>
    array.sort(
      (a, b) =>
        order.map(category => category.id).indexOf(a.id) -
        order.map(category => category.id).indexOf(b.id),
    );

  return (
    <Layout seo={seoMetaTags} page={datoCmsBlog} isBlogDetail={true}>
      <HelmetDatoCms>
        <script type="application/ld+json">
          {`{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "${title}",
          "image": [
            "${featuredImage.gatsbyImageData.images.fallback.src}"
          ],
          "datePublished": "${meta.firstPublishedAt}",
          "dateModified": "${meta.updatedAt}",
          "author": [{
              "@type": "Person",
              "name": "${author.title}",
              "url": "${
                process.env.GATSBY_SITE_URL + buildUrl(author.slug, author)
              }"
            }]
        }`}
        </script>
      </HelmetDatoCms>
      <main>
        <BlogDetailBanner blogPost={datoCmsBlog} resource={featuredResource} />
        <BlogDetail
          blogPost={datoCmsBlog}
          resource={featuredResource}
          introduction={introduction}
          anchorLinks={anchorLinks}
          items={modularBlocks}
          summary={summary}
          summaryNew={summaryNew}
          author={author}
          title={title}
        />
      </main>
      <FeaturedBlogPosts
        heading="More recent posts"
        items={otherBlogPosts}
        isBlogDetail={true}
      />
      {orderBlogCategories(blogCategories, categories).map(
        ({ blogPostsModularBlocks }, id) => (
          <ModularBlocks key={id} items={blogPostsModularBlocks} />
        ),
      )}
      <CallToAction
        image={ctaImage}
        heading={ctaHeading}
        text={ctaText}
        link={ctaLink}
      />
      <FeaturedResourcePopUp {...featuredResource} />
    </Layout>
  );
};

export const BlogDetailPageTemplateQuery = graphql`
  query BlogDetailPageTemplateQuery($id: String!, $categories: [String]!) {
    datoCmsBlog(id: { eq: $id }) {
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      title
      ...BlogCardFragment
      introduction {
        value
        links {
          id: originalId
          text
          pageUrl {
            ...LinkFragment
          }
        }
      }
      modularBlocks {
        ...BlogFeaturedResourceModularBlockFragment
        ...ContainedImageModularBlockFragment
        ...ContainedImageNarrowerModularBlockFragment
        ...ContentModularBlockFragment
        ...ExternalVideoModularBlockFragment
        ...HtmlModularBlockFragment
        ...InternalVideoModularBlockFragment
        ...NewsletterSignupModularBlockFragment
        ...TableModularBlockFragment
      }
      featuredResource {
        ...FeaturedResourceFragment
      }
      meta {
        firstPublishedAt
        updatedAt
      }
      ...LinkFragment
    }
    datoCmsBlogSitewide {
      ctaImage {
        url
        alt
      }
      ctaHeading
      ctaText
      ctaLink {
        text
        pageUrl {
          ...LinkFragment
        }
      }
    }
    allDatoCmsBlog(
      filter: { id: { ne: $id } }
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
    ) {
      otherBlogPosts: nodes {
        ...BlogCardFragment
      }
    }
    allDatoCmsBlogCategory(filter: { id: { in: $categories } }) {
      blogCategories: nodes {
        id
        blogPostsModularBlocks {
          ...CallToActionModularBlockFragment
          ...CallToActionStripModularBlockFragment
          ...ContentModularBlockFragment
          ...ImageContentModularBlockV1Fragment
          ...ImageContentModularBlockV2Fragment
          ...ImageContentModularBlockIllustrationFragment
        }
      }
    }
  }
`;

export default BlogDetailPageTemplate;
